import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Back, Empty, Inline, Primary, Secondary, Tag, day, money, useQuery } from "@/components/backoffice/kit";

const dash = (v, why) => (v == null ? <span title={why}>—</span> : money(v));

function StripeCard({ info, onConnect, onDashboard, busy }) {
    const s = info?.status || {};
    if (s.house) {
        return (
            <div className="border border-[#1a1a1a] bg-white p-3" data-testid="stripe-card">
                <Tag tone="green">Connected · platform account</Tag>
                <p className="mt-1 text-[15px]">This shop charges on 13thav's own Stripe account. Card money lands there; see the Stripe dashboard.</p>
            </div>
        );
    }
    const state = s.charges_enabled && s.payouts_enabled ? "ok" : s.connected ? "info" : "none";
    return (
        <div className="border border-[#1a1a1a] bg-white p-3" data-testid="stripe-card" data-state={state}>
            {state === "ok" && <Tag tone="green" testid="stripe-state">Connected · payouts on</Tag>}
            {state === "info" && <Tag tone="red" testid="stripe-state">Stripe needs info</Tag>}
            {state === "none" && <Tag tone="grey" testid="stripe-state">Not connected</Tag>}
            <p className="mt-1 text-[15px] text-[#4d4d4d]">
                {state === "ok" ? "Card money for your jobs lands in your own Stripe account." : state === "info" ? "Stripe still needs a detail or two before payouts switch on." : "Connect Stripe to take cards and get paid."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
                {state === "ok" && <Primary onClick={onDashboard} disabled={busy} testid="stripe-dashboard">Open Stripe dashboard</Primary>}
                {state === "info" && <Primary onClick={onConnect} disabled={busy} testid="stripe-finish">Finish in Stripe</Primary>}
                {state === "none" && <Primary onClick={onConnect} disabled={busy} testid="stripe-connect">Connect Stripe</Primary>}
                {state === "info" && <Secondary onClick={onDashboard} disabled={busy} testid="stripe-dashboard">Open Stripe dashboard</Secondary>}
            </div>
        </div>
    );
}

/** 8.6 Payouts — read-only money facts: Stripe card, balance, upcoming, history (tap → the orders in it). */
export default function PayoutsScreen() {
    const { params, set, pathname } = useQuery();
    const pick = params.get("payout");
    const [info, setInfo] = useState(null);
    const [bal, setBal] = useState(null);
    const [hist, setHist] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => {
        setErr("");
        Promise.all([api.get("/store/stripe"), api.get("/store/stripe/balance"), api.get("/store/payouts")])
            .then(([a, b, c]) => { setInfo(a.data); setBal(b.data); setHist(c.data); })
            .catch((e) => { setErr(formatApiError(e.response?.data?.detail)); setInfo({}); setBal({}); setHist({ statements: [] }); });
    }, []);
    useEffect(() => { load(); }, [load]);
    const connect = async () => {
        setBusy(true);
        try { const { data } = await api.post("/store/stripe/connect", { origin_url: window.location.origin }); window.location.href = data.url; }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
    };
    const dashboard = async () => {
        setBusy(true);
        try { const { data } = await api.post("/store/stripe/dashboard"); window.open(data.url, "_blank", "noopener"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    if (info === null) return <div className="flex min-h-[30vh] items-center justify-center" data-testid="payouts-loading"><Loader2 className="animate-spin" /></div>;
    const statements = hist?.statements || [];
    if (pick) {
        const st = statements.find((s) => s.id === pick);
        return (
            <div className="grid gap-3" data-testid="payout-detail">
                <Back to={pathname} testid="payout-back" />
                {!st ? <Empty title="That payout isn't in the list." /> : (
                    <>
                        <h1 className="text-[22px] font-semibold">{st.label}</h1>
                        <p className="text-[15px]">{st.count} order{st.count === 1 ? "" : "s"} · gross {money(st.gross)} · fees {money(st.fee)} · net <b>{money(st.net)}</b></p>
                        <ul className="divide-y divide-[#767676] border-y border-[#767676]" data-testid="payout-orders">
                            {st.rows.map((r) => (
                                <li key={r.order_number} className="flex flex-wrap items-center gap-x-3 py-2 text-[15px]">
                                    <a href={`/account/orders?f=open&q=${r.order_number}`} className="font-semibold underline">{r.order_number}</a>
                                    <span className="text-[#4d4d4d]">{r.customer} · {day(r.date)} · {r.method}</span>
                                    <span className="ml-auto tabular-nums">{money(r.net)}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        );
    }
    return (
        <div className="grid gap-4" data-testid="payouts-screen">
            <h1 className="text-[22px] font-semibold">Payouts</h1>
            <Inline error={err} onRetry={load} testid="payouts-error" />
            <StripeCard info={info} onConnect={connect} onDashboard={dashboard} busy={busy} />
            <div className="grid gap-2 sm:grid-cols-3" data-testid="payouts-balance">
                <div className="border border-[#1a1a1a] p-3"><p className="text-[13px] font-semibold uppercase text-[#4d4d4d]">Available</p><p className="text-[22px] font-semibold" data-testid="balance-available">{dash(bal?.available, bal?.why)}</p></div>
                <div className="border border-[#1a1a1a] p-3"><p className="text-[13px] font-semibold uppercase text-[#4d4d4d]">Pending</p><p className="text-[22px] font-semibold" data-testid="balance-pending">{dash(bal?.pending, bal?.why)}</p></div>
                <div className="border border-[#1a1a1a] p-3"><p className="text-[13px] font-semibold uppercase text-[#4d4d4d]">Upcoming</p>
                    <p className="text-[22px] font-semibold" data-testid="balance-upcoming">{bal?.next_amount != null ? money(bal.next_amount) : "—"}</p>
                    {bal?.next_date && <p className="text-[14px] text-[#4d4d4d]">{day(bal.next_date)}</p>}
                </div>
            </div>
            {bal?.why && <p className="text-[14px] text-[#4d4d4d]" data-testid="balance-why">{bal.why}</p>}
            <section>
                <h2 className="text-[18px] font-semibold">History</h2>
                {statements.length === 0 ? <p className="text-[15px] text-[#4d4d4d]" data-testid="payouts-none">No paid orders yet.</p> : (
                    <ul className="divide-y divide-[#767676] border-y border-[#767676]" data-testid="payouts-history">
                        {statements.map((s) => (
                            <li key={s.id}>
                                <button onClick={() => set({ payout: s.id })} data-testid={`payout-row-${s.id}`} className="flex min-h-[44px] w-full flex-wrap items-center gap-x-3 py-2 text-left text-[15px]">
                                    <span className="font-semibold">{s.label}</span>
                                    <span className="text-[#4d4d4d]">{s.count} order{s.count === 1 ? "" : "s"} · {s.state.replace("_", " ")}</span>
                                    <span className="ml-auto tabular-nums font-semibold">{money(s.net)}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {(hist?.stripe_payouts || []).length > 0 && (
                    <ul className="mt-3 divide-y divide-[#767676] border-y border-[#767676]" data-testid="stripe-payouts">
                        {hist.stripe_payouts.map((p) => <li key={p.id} className="flex items-center gap-3 py-2 text-[15px]"><span>Stripe payout · {day(p.arrival)}</span><span className="text-[#4d4d4d]">{p.status}</span><span className="ml-auto tabular-nums">{money(p.amount)}</span></li>)}
                    </ul>
                )}
            </section>
        </div>
    );
}
